import { ApiDelete, ApiGet, ApiPost, ApiPut } from "./axiosFunctions";

export const DEFAULT_ADMIN_PASSWORD = "Admin@123456";

export function getApiError(error, fallback = "Request failed") {
  return (
    error?.response?.data?.data?.errorDescription ||
    error?.response?.data?.data?.error ||
    error?.response?.data?.errorDescription ||
    error?.response?.data?.error ||
    error?.data?.errorDescription ||
    error?.data?.error ||
    error?.message ||
    fallback
  );
}

export function mapUiLanguage(language) {
  const code = String(language || "en").toLowerCase();
  if (code.startsWith("lt")) return "lt";
  if (code.startsWith("es")) return "es";
  return "en";
}

export function mapPreferredLanguage(language) {
  const code = String(language || "en").toLowerCase();
  if (code.startsWith("lt")) return "lt_LT";
  if (code.startsWith("es")) return "es_ES";
  return "en_GB";
}

export function getStudentId(student) {
  return student?.studentId || student?.userId || student?.id || "";
}

export function getStudentSchoolId(student, fallbackSchoolId) {
  return student?.schoolId || fallbackSchoolId || "";
}

export function formatStudentLabel(student) {
  const name = `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
  const email = student?.email || student?.username || "";
  if (name && email) return `${name} (${email})`;
  return name || email || getStudentId(student) || "Student";
}

function matchesStudentQuery(student, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  return [
    student?.firstName,
    student?.lastName,
    student?.email,
    student?.username,
    student?.nickname,
    student?.studentId,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

function dedupeStudents(list) {
  const seen = new Set();
  return (list || []).filter((student) => {
    const id = getStudentId(student);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

async function postStudentSearch(body, limit) {
  const response = await ApiPost(
    `/admins/students/search?offset=0&limit=${limit}`,
    body
  );
  return parseListResponse(response);
}

async function listSchoolStudents(schoolId, limit) {
  if (!schoolId) return [];
  const response = await ApiGet(`/admins/students/schools/${schoolId}`, {
    offset: 0,
    limit,
  });
  return parseListResponse(response);
}

/**
 * Student picker for parent create.
 * Uses POST /admins/students/search (AND fields only when set)
 * plus GET /admins/students/schools/:schoolId for the school roster.
 */
export async function searchStudentsForPicker({
  query = "",
  schoolId,
  limit = 50,
} = {}) {
  const q = String(query || "").trim();
  const results = [];

  const trySearch = async (body) => {
    try {
      return await postStudentSearch(body, limit);
    } catch {
      return [];
    }
  };

  if (q.includes("@")) {
    results.push(...(await trySearch({ ...(schoolId ? { schoolId } : {}), email: q })));
    if (results.length === 0) {
      results.push(...(await trySearch({ email: q })));
    }
  } else if (q) {
    const parts = q.split(/\s+/).filter(Boolean);
    const nameBody = {
      ...(schoolId ? { schoolId } : {}),
      firstName: parts[0],
    };
    if (parts.length > 1) nameBody.lastName = parts.slice(1).join(" ");
    results.push(...(await trySearch(nameBody)));
    if (results.length === 0 && parts.length === 1) {
      results.push(...(await trySearch({ ...(schoolId ? { schoolId } : {}), lastName: parts[0] })));
    }
    if (results.length === 0) {
      const unscoped = { firstName: parts[0] };
      if (parts.length > 1) unscoped.lastName = parts.slice(1).join(" ");
      results.push(...(await trySearch(unscoped)));
    }
  } else if (schoolId) {
    results.push(...(await trySearch({ schoolId })));
  }

  try {
    const roster = await listSchoolStudents(schoolId, Math.max(limit, 100));
    results.push(...(q ? roster.filter((student) => matchesStudentQuery(student, q)) : roster));
  } catch {
    // school roster is optional if search already returned students
  }

  return dedupeStudents(results).slice(0, limit);
}

export function parseListResponse(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.contentCreators)) return payload.contentCreators;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.students)) return payload.data.students;
  if (Array.isArray(payload?.data?.contentCreators)) return payload.data.contentCreators;
  return [];
}

export function parseTotalCount(response, fallback = 0) {
  const payload = response?.data ?? response;
  const candidates = [
    payload?.total,
    payload?.totalCount,
    payload?.totalElements,
    payload?.count,
    payload?.numberOfElements,
    payload?.pagination?.total,
    payload?.pagination?.totalCount,
    payload?.page?.totalElements,
    payload?.data?.total,
    payload?.data?.totalCount,
    payload?.data?.totalElements,
    payload?.data?.count,
    payload?.data?.pagination?.total,
  ];
  const total = candidates.find((value) => Number.isFinite(Number(value)));
  return total === undefined ? fallback : Number(total);
}

export async function searchStudents({ offset = 0, limit = 10, schoolId } = {}) {
  if (schoolId) {
    return ApiGet(`/admins/students/schools/${schoolId}`, { offset, limit });
  }
  return ApiGet(`/admins/students`, { offset, limit });
}

export async function listStudentsBySchool(schoolId, { offset = 0, limit = 10 } = {}) {
  return ApiGet(`/admins/students/schools/${schoolId}`, { offset, limit });
}

export async function createStudent(payload) {
  return ApiPost("/admins/students", payload);
}

export async function searchTeachers({ offset = 0, limit = 10, schoolId } = {}) {
  if (schoolId) {
    return ApiGet(`/admins/teachers/schools/${schoolId}`, { offset, limit });
  }
  return ApiGet(`/admins/teachers`, { offset, limit });
}

export async function createTeacher(payload) {
  return ApiPost("/admins/teachers", payload);
}

export function getTeacherId(teacher) {
  return teacher?.teacherId || teacher?.userId || teacher?.id || "";
}

export async function getTeacher(teacherId) {
  return ApiGet(`/admins/teachers/${teacherId}`);
}

export async function updateTeacher(payload) {
  return ApiPut("/admins/teachers", payload);
}

export async function deleteTeacher(teacherId) {
  return ApiDelete(`/admins/teachers/${teacherId}`);
}

export async function searchParents({ offset = 0, limit = 10, schoolId } = {}) {
  if (schoolId) {
    return ApiGet(`/admins/parents/schools/${schoolId}`, { offset, limit });
  }
  return ApiPost(`/admins/parents/search?offset=${offset}&limit=${limit}`, {});
}

export async function createParent(payload) {
  return ApiPost("/admins/parents", payload);
}

function compactSearchBody(fields = {}) {
  const body = {};
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return;
      body[key] = trimmed;
      return;
    }
    if (Array.isArray(value) && value.length === 0) return;
    body[key] = value;
  });
  return body;
}

function applyNameQuery(body, query) {
  const q = String(query || "").trim();
  if (!q) return body;
  if (q.includes("@")) {
    body.email = q;
    return body;
  }
  const parts = q.split(/\s+/).filter(Boolean);
  body.firstName = parts[0];
  if (parts.length > 1) body.lastName = parts.slice(1).join(" ");
  return body;
}

const CREATOR_SEARCH_LANGUAGES = [
  "en",
  "lt",
  "es",
  "en_GB",
  "lt_LT",
  "es_ES",
  "en_UK",
  "en_US",
];
const CREATOR_SEARCH_COUNTRIES = ["LT", "GB", "ES", "IE"];

function getCreatorId(creator) {
  return (
    creator?.contentCreatorId ||
    creator?.userId ||
    creator?.id ||
    creator?.email ||
    creator?.username ||
    ""
  );
}

function dedupeCreators(list) {
  const seen = new Set();
  return (list || []).filter((creator) => {
    const id = getCreatorId(creator);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * POST /admins/content-creators/search
 * Empty {} returns 400. Postman TEST sends preferredLanguage and/or countryCode.
 */
export async function searchContentCreators({
  offset = 0,
  limit = 10,
  query,
  firstName,
  lastName,
  email,
  username,
  preferredLanguage,
  countryCode,
} = {}) {
  const body = applyNameQuery(
    compactSearchBody({
      firstName,
      lastName,
      email,
      username,
      preferredLanguage,
      countryCode,
    }),
    query
  );

  if (Object.keys(body).length === 0) {
    body.preferredLanguage = "en";
  }

  return ApiPost("/admins/content-creators/search", body, { offset, limit });
}

async function searchCreatorsByFilter(filter, { offset = 0, limit = 100 } = {}) {
  try {
    const response = await ApiPost(
      "/admins/content-creators/search",
      compactSearchBody(filter),
      { offset, limit }
    );
    return {
      items: parseListResponse(response),
      total: parseTotalCount(response),
    };
  } catch {
    return { items: [], total: 0 };
  }
}

async function searchCreatorsAllPages(filter, pageSize = 100, maxPages = 20) {
  const collected = [];
  let offset = 0;
  for (let page = 0; page < maxPages; page += 1) {
    const { items, total } = await searchCreatorsByFilter(filter, {
      offset,
      limit: pageSize,
    });
    collected.push(...items);
    offset += items.length;
    if (items.length < pageSize) break;
    if (total && offset >= total) break;
  }
  return collected;
}

/** Load every creator the search API can return (all languages + countries). */
export async function fetchAllContentCreators({ query } = {}) {
  if (query && String(query).trim()) {
    const response = await searchContentCreators({
      query,
      offset: 0,
      limit: 100,
    });
    return parseListResponse(response);
  }

  const filters = [
    ...CREATOR_SEARCH_LANGUAGES.map((preferredLanguage) => ({ preferredLanguage })),
    ...CREATOR_SEARCH_COUNTRIES.map((countryCode) => ({ countryCode })),
  ];

  const batches = await Promise.all(
    filters.map((filter) => searchCreatorsAllPages(filter))
  );
  return dedupeCreators(batches.flat()).sort((a, b) => {
    const aDate = new Date(a.creationDate || a.createdAt || 0).getTime();
    const bDate = new Date(b.creationDate || b.createdAt || 0).getTime();
    return bDate - aDate;
  });
}

export async function createContentCreator(payload) {
  return ApiPost("/admins/content-creators", payload);
}

export function getContentCreatorId(creator) {
  return getCreatorId(creator);
}

export function parseItemResponse(response) {
  const payload = response?.data ?? response;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
      return payload.data;
    }
    return payload;
  }
  return null;
}

export async function getContentCreator(contentCreatorId) {
  return ApiGet(`/admins/content-creators/${contentCreatorId}`);
}

export async function updateContentCreator(payload) {
  return ApiPut("/admins/content-creators", payload);
}

export async function deleteContentCreator(contentCreatorId) {
  return ApiDelete(`/admins/content-creators/${contentCreatorId}`);
}
